/**
 * Colour helpers for the branding tab and booth theming.
 *
 * The Site Builder lets operators pick arbitrary colours, which means they can
 * and will produce unreadable combinations. `readableTextOn` gives the booth a
 * sane default so a dark brand colour never yields black-on-black text.
 */

export function normaliseHex(value: string): string {
  const trimmed = value.trim();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  if (/^#[0-9a-f]{3}$/i.test(withHash)) {
    const [r, g, b] = withHash.slice(1);
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return /^#[0-9a-f]{6}$/i.test(withHash) ? withHash.toLowerCase() : "";
}

export function isValidHex(value: string): boolean {
  return normaliseHex(value) !== "";
}

function channels(hex: string): [number, number, number] {
  const normalised = normaliseHex(hex) || "#000000";
  return [
    parseInt(normalised.slice(1, 3), 16),
    parseInt(normalised.slice(3, 5), 16),
    parseInt(normalised.slice(5, 7), 16),
  ];
}

/** WCAG relative luminance. */
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [lighter, darker] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (lighter + 0.05) / (darker + 0.05);
}

export function readableTextOn(background: string): "#000000" | "#ffffff" {
  return luminance(background) > 0.45 ? "#000000" : "#ffffff";
}

export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = channels(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── The console wearing an event's colour ───────────────────────────────────

function toHsl(hex: string): [number, number, number] {
  const [r, g, b] = channels(hex).map((c) => c / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return [0, 0, l];

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return [h, s, l];
}

function fromHsl(h: number, s: number, l: number): string {
  if (s === 0) {
    const v = Math.round(l * 255);
    return `#${[v, v, v].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };

  const rgb = [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)]
    .map((c) => Math.round(c * 255));
  return `#${rgb.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

const triplet = (hex: string) => channels(hex).join(" ");

/** The console's accent tokens, as space-separated RGB for the CSS variables. */
export interface AccentTokens {
  "--c-accent": string;
  "--c-accent-bright": string;
  "--c-accent-dim": string;
  "--c-accent-ink": string;
}

/**
 * Turns an event's brand colour into a usable console accent.
 *
 * ## Why this isn't just "set the variable"
 *
 * A brand colour is chosen to look good as a *booth background*, filling a
 * screen. The console uses its accent as small text, thin borders and focus
 * rings on a near-black or near-white page — a completely different job.
 *
 * Lime (#d4f14f) is gorgeous behind a photo and invisible as 11px text on white.
 * Navy (#0b1d51) is the reverse. Applying either one raw would take a console
 * that passes contrast everywhere and quietly break every label in it.
 *
 * So the hue is kept — that's the part that reads as "this is Heineken" — and
 * the lightness is walked until it clears 3:1 against the page it will sit on.
 * If a colour can't get there in 40 steps (a pure mid-grey, essentially) the
 * caller gets null and the console keeps its own accent rather than shipping
 * something unreadable.
 *
 * @param hex   the event's primary colour
 * @param page  the page background it must be legible against
 */
export function accentTokens(hex: string, page: string): AccentTokens | null {
  const normalised = normaliseHex(hex);
  if (!normalised) return null;

  const [h, s] = toHsl(normalised);

  // A greyscale brand colour cannot be an accent. Walking a grey to legibility
  // just produces a different grey, indistinguishable from body text — so the
  // console would look broken rather than branded. Keep our own accent.
  if (s < 0.15) return null;

  const pageIsLight = luminance(page) > 0.45;

  // The accent has to satisfy TWO things, not one:
  //   * legible AS text and borders on the page          (>= 3:1 vs page)
  //   * legible with text ON it, since it fills buttons  (>= 4.5:1 vs its ink)
  //
  // Only checking the first is a trap. Gold (#d4a017) clears 8:1 against a dark
  // page and then gives you a button whose label sits at 2.4:1 — unreadable, on
  // the most important control in the console. Mid-tones are exactly where this
  // bites, and mid-tones are common brand colours.
  const inkContrast = (colour: string) =>
    Math.max(contrastRatio("#000000", colour), contrastRatio("#ffffff", colour));
  const good = (colour: string) => contrastRatio(colour, page) >= 3 && inkContrast(colour) >= 4.5;

  // Walk away from the page's luminance: darker on a light page, lighter on a
  // dark one. Conveniently that also walks away from the mid-tones that fail the
  // second test. Small steps, so we stop at the first value that works and stay
  // as close to the operator's actual colour as possible.
  let best = normalised;
  for (let i = 0; i <= 60 && !good(best); i += 1) {
    const [, , currentL] = toHsl(best);
    const nextL = pageIsLight ? currentL - 0.015 : currentL + 0.015;
    if (nextL <= 0.02 || nextL >= 0.98) break;
    best = fromHsl(h, s, nextL);
  }

  // Nowhere left to go. Don't force it — an unreadable console is worse than an
  // off-brand one.
  if (!good(best)) return null;

  const [, , l] = toHsl(best);
  const bright = fromHsl(h, s, Math.min(0.96, l + (pageIsLight ? -0.06 : 0.08)));
  const dim = fromHsl(h, s, Math.max(0.06, l + (pageIsLight ? 0.08 : -0.14)));

  return {
    "--c-accent": triplet(best),
    "--c-accent-bright": triplet(bright),
    "--c-accent-dim": triplet(dim),
    // Whichever of black or white actually contrasts better with the adjusted
    // accent — not `readableTextOn`, whose luminance threshold picks a side
    // without checking it clears 4.5:1.
    "--c-accent-ink": triplet(
      contrastRatio("#000000", best) >= contrastRatio("#ffffff", best) ? "#000000" : "#ffffff",
    ),
  };
}
